import { IUpdateTagGroup, TagGroup } from 'contensis-management-api';
import pl from 'pluralize';

import MigratortronBaseService from './MigratortronBaseService';
import TagsService from './TagsService';
import {
  MigrateRequest,
  ProjectMigrateTagGroup,
  TagGroupsResult,
} from '~/models/Operations';
import ContensisRepository from '~/repositories/ContensisRepository';
import {
  logMigrateResultsTable,
  logTagGroupsPreviewTable,
} from '~/transformations/logging';
import { TagGroupBuilder } from '~/transformations/TagGroupBuilder';
import { dedupeInner } from '~/util/array';
import {
  completeProgressBar,
  logError,
  Logger,
  newProgressBar,
} from '~/util/logger';

export type GetTagGroupsArgs = {
  id?: string;
  language?: string;
  q?: string;
  ids?: string[];
};

class TagGroupsMigrationService extends MigratortronBaseService {
  /** Keep a note of the tag groups we have migrated in this job separate from the tags migration count */
  groupsMigration: {
    created: string[];
    updated: string[];
    deleted: string[];
  } = {
    created: [],
    updated: [],
    deleted: [],
  };

  constructor(payload: MigrateRequest, preview: boolean) {
    super(payload, preview);
  }

  GetTagGroups = async ({ id, language, q }: GetTagGroupsArgs = {}) => {
    // Just gets tags from source repo
    return await this.source.tags.GetTagGroups(id || { language, q });
  };

  MigrateTagGroups = async (getBy: GetTagGroupsArgs = {}) => {
    const { isPreview } = this;

    // Loads content types and components into memory
    this.payload.includeDefaults = false;
    if (!this.isHydrated) await this.HydrateContensisRepositories();

    // Loads all tag groups from source and target repos into memory
    await this.GetTagGroupsToMigrate(getBy);

    // Build and store the target tag groups
    // against each targetRepo.migrateGroups
    await this.BuildTagGroupsToMigrate();

    const previewResult = this.mapGroupsMigrateResult();
    if (Logger.shouldLog('warning')) {
      logTagGroupsPreviewTable(previewResult, Object.keys(this.targets));
    }

    if (!isPreview && this.errorCount && !this.ignoreErrors) {
      Logger.error(
        `[taggroups] Not committing ${this.opName} due to ${this.errorCount} errors`
      );
    } else if (!isPreview) {
      if (this.ignoreErrors)
        Logger.warning(
          `[taggroups] Committing ${this.opName} with ${this.errorCount} known errors`
        );

      if (!isPreview) {
        await this.CommitTagGroups();
      }
    }

    let result = previewResult;
    if (!isPreview) {
      result = this.mapGroupsMigrateResult();
    }

    logMigrateResultsTable(result, 'taggroups');

    Logger.success(
      `Tag groups ${this.opName} ${isPreview ? `preview ready` : 'complete'}`,
      true
    );

    return result;
  };

  GetTagGroupsToMigrate = async ({
    id,
    ids,
    language,
    q,
  }: GetTagGroupsArgs = {}) => {
    const { source, targets } = this;

    const hasTagGroupsInPayload = source.tags.groups?.length > 0;

    // Tag groups are not supplied in the payload
    // Tags may be supplied in the payload and no source is available
    if (!hasTagGroupsInPayload && source.repo) {
      // Get tag groups from source repo
      await source.tags.GetTagGroups(id || { ids, language, q });
    }

    if (source.tags.groups.length > 0) {
      const sourceGroupIds = [
        ...new Set(
          [
            ...source.tags.groups.map((g) => g.id),
            ...source.tags.raw.map((t) => t.groupId),
          ].filter((id) => id)
        ),
      ];
      for (const [, target] of Object.entries(targets)) {
        // Fetch tag groups from target repo
        await target.tags.GetTagGroups({
          ids: sourceGroupIds,
        });
      }
    }
  };

  BuildTagGroupsToMigrate = async () => {
    // Build tag groups for each target repo
    for (const [, target] of Object.entries(this.targets)) {
      await target.tags.BuildTagGroups({
        onError: (b) => this.AddError(b.id, b.error),
      });
    }
  };

  CommitTagGroups = async () => {
    for (const [, target] of Object.entries(this.targets)) {
      await this.QueueTagGroups(target);
    }
  };

  QueueTagGroups = async (target: ContensisRepository) => {
    const migrateGroups = target.tags.migrateGroups.filter((mg) =>
      ['create', 'update'].includes(mg.status)
    );
    const totalGroups = migrateGroups.length;

    if (totalGroups)
      Logger.info(
        `[taggroups] Committing ${totalGroups} ${pl('tag group', totalGroups)} in ${Logger.highlightText(
          target.alias
        )} ${Logger.standardText(target.project.id)}`,
        true
      );

    const pr = newProgressBar(
      ` Loading [${target.project.id}] :bar :percent ${Logger.infoText(
        ':current/:total :elapseds/:etas :ratep/s'
      )}`,
      {
        output: this.payload.outputProgress,
        complete: Logger.successText('='),
        head: Logger.successText('>'),
        incomplete: ' ',
        width: 30,
        total: totalGroups,
      }
    );

    let currentIndex = 0;

    for (const migrateGroup of migrateGroups) {
      // use queue so we can load tag groups in parallel
      void this.queue.add(async () => {
        Logger.info(
          `[taggroups] ${++currentIndex}/${totalGroups} [${
            migrateGroup.status
          }] '${migrateGroup.source.name}' | ${migrateGroup.id}`
        );
        await this.CommitTagGroup(migrateGroup, target);
        pr?.tick();
      });
    }

    await this.queue.onIdle();

    completeProgressBar();
  };

  CommitTagGroup = async (
    migrateGroup: TagGroupBuilder,
    target: ContensisRepository
  ): Promise<[null, TagGroup] | [AppError, undefined]> => {
    const sourceTagGroup = migrateGroup.source;

    const upsertTagGroup = migrateGroup.toJSON();

    // Upsert tag group
    const [error, newTagGroup] = await target.repo.UpdateOrCreateTagGroup(
      upsertTagGroup,
      migrateGroup.existing
    );

    if (error) {
      const mappedError = this.AddError(sourceTagGroup.id, error);
      migrateGroup.error = mappedError;
      logError(
        error.data || error,
        `${mappedError.message} | ${upsertTagGroup.id}`
      );
      return [error, newTagGroup];
    }

    if (migrateGroup.status === 'create') {
      this.groupsMigration.created.push(sourceTagGroup.id);
    } else if (migrateGroup.status === 'update') {
      this.groupsMigration.updated.push(sourceTagGroup.id);
    }
    return [error, newTagGroup];
  };

  private mapGroupsMigrateResult = () => {
    const { source, targets } = this;
    const sourceProjectId =
      source.projectId && source.projectId !== 'null'
        ? source.projectId
        : Object.values(targets)[0].projectId;

    const result = {
      groups: sourceProjectId
        ? {
            [sourceProjectId]: {
              ...Object.fromEntries(
                source.tags.groups.map((g) => [g.id, g.tagCount || 0])
              ),
              totalCount: source.tags.groups.length,
            },
          }
        : undefined,
      errors: Object.keys(this.errors).length
        ? dedupeInner(Object.values(this.errors).flat())
        : undefined,
      groupsResult: !this.isPreview
        ? {
            created: this.groupsMigration.created.length,
            updated: this.groupsMigration.updated.length,
            deleted: this.groupsMigration.deleted.length,
            errors: Object.values(this.errors).flat().length || undefined,
          }
        : undefined,
      existing: Object.fromEntries(
        Object.keys(targets).map((projectId) => [projectId, { totalCount: 0 }])
      ),
      groupsToMigrate: Object.fromEntries(
        Object.keys(targets).map((projectId) => [projectId, {}])
      ),
    } as TagGroupsResult;

    const sourceGroups =
      this.opName === 'delete'
        ? Object.values(targets)
            .map((target) => target.tags.migrateGroups.map((mg) => mg.source))
            .flat()
        : source.tags.groups;

    result.groupsToMigrate.groupIds = Object.fromEntries(
      sourceGroups.map((sourceGroup) => [
        sourceGroup.id,
        {
          name: sourceGroup.name,
          description: sourceGroup.description,
          ...Object.fromEntries(
            Object.entries(targets)
              .map(([projectId, contensis]) => {
                const mg = contensis.tags.migrateGroups.find(
                  (mg) => mg.id === sourceGroup.id
                );
                if (!mg) return [];
                return [
                  projectId,
                  {
                    status: mg.status,
                    diff: mg.diff,
                    error: mg.error?.message,
                  },
                ];
              })
              .filter((v) => v.length) as [string, ProjectMigrateTagGroup][]
          ),
        },
      ])
    );
    for (const [projectId, target] of Object.entries(targets)) {
      result.existing[projectId] = {
        totalCount: target.tags.groups.length,
        groups: target.tags.groups,
      };

      const statuses = [
        ...new Set(target.tags.migrateGroups.map((mg) => mg.status)),
      ];
      result.groupsToMigrate[projectId] = Object.fromEntries([
        ...statuses.map((status) => [
          status,
          target.tags.migrateGroups.filter((mg) => mg.status === status).length,
        ]),
        [
          'totalCount',
          target.tags.migrateGroups?.filter((mg) =>
            ['create', 'update', 'delete'].includes(mg.status)
          )?.length || 0,
        ],
      ]);
    }

    return result;
  };

  DeleteTagGroups = async (getBy: GetTagGroupsArgs, targets = this.targets) => {
    const { isPreview } = this;
    this.opName = 'delete';

    // Loads all tag groups from source and target repos into memory
    await this.GetTagGroupsToDelete(getBy, targets);

    if (!isPreview) {
      for (const [projectId, target] of Object.entries(targets)) {
        Logger.info(
          `Deleting tag groups in project ${Logger.highlightText(projectId)}`,
          true
        );

        for (const [i, migrateGroup] of Object.entries(
          target.tags.migrateGroups?.filter(
            (mg) => mg.status === 'delete' && !!mg.existing
          ) || []
        )) {
          void this.queue.add(async () => {
            const group = migrateGroup.existing as TagGroup;
            Logger.warning(
              `[delete] ${Number(i) + 1}/${
                target.tags.groups.length
              } ${projectId} [${group.id}] ${group.name}`
            );
            const [error] = await target.repo.DeleteTagGroup(group.id);
            if (error) {
              Logger.error(
                `Error attempting to delete tag ${group.id}. Error was ${JSON.stringify(error)}`
              );
              const mappedError = this.AddError(group.id, error);
              migrateGroup.error = mappedError;
              migrateGroup.status = 'error';
            } else this.groupsMigration.deleted.push(group.id);
          });
        }
      }
    }

    await this.queue.onIdle();

    Logger.info(
      `Delete tag groups ${isPreview ? 'preview ready' : 'completed'}`,
      true
    );
    return this.mapGroupsMigrateResult();
  };

  GetTagGroupsToDelete = async (
    { id, ids, language, q }: GetTagGroupsArgs,
    targets = this.targets
  ) => {
    const { source } = this;
    const promises = [] as Promise<any>[];

    for (const [, target] of Object.entries(targets)) {
      promises.push(
        (async () => {
          const [err, deleteGroups] = await target.tags.GetTagGroups(
            id || { ids, language, q }
          );
          if (err) this.AddError('*', err);
          else {
            deleteGroups.map((group) => {
              const tagGroupBuilder = new TagGroupBuilder(
                target,
                source,
                group
              );
              tagGroupBuilder.existing = group;
              if (group.tagCount) {
                tagGroupBuilder.status = 'error';
                tagGroupBuilder.error = new Error(
                  `Cannot delete tag group that is currently in use`
                );
                this.AddError(tagGroupBuilder.id, tagGroupBuilder.error);
              } else tagGroupBuilder.status = 'delete';
              target.tags.migrateGroups.push(tagGroupBuilder);
            });
          }
        })()
      );
    }

    // Get all repo tags at the same time
    await Promise.all(promises);
  };
}

export default TagGroupsMigrationService;
